/* ----------------------------- R2 Configuration ----------------------------- */
import { toStaticServiceUrl } from "@/utils/staticApiRouter";

const R2_CONFIG = {
    PUBLIC_BASE_URL: "https://pub-320f2a843b0d4bf894bd8fb2c9e7e1e4.r2.dev",
    PLAYLIST_IMAGES_PATH: "/playlist_images",
    FLAGS_PATH: "/flags",
    UPLOADS_PATH: "/uploads",
    DEFAULT_IMAGE: "default.png",
    DEFAULT_FLAG: "default.png"
} as const;

/* ----------------------------- Config ----------------------------- */
const ENV_API_ORIGIN = (import.meta as any)?.env?.VITE_API_ORIGIN as string | undefined;
const BROWSER_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const API_ORIGIN = (
    ((import.meta as any)?.env?.DEV ? BROWSER_ORIGIN : ENV_API_ORIGIN) ||
    ENV_API_ORIGIN ||
    BROWSER_ORIGIN
).replace(/\/+$/, "");

const buildApiUrl = (path: string): string => {
    if (!path.startsWith("/")) return path;
    // Prefer explicit backend origin when provided; otherwise use same-origin proxy.
    return API_ORIGIN ? `${API_ORIGIN}${path}` : path;
};

export const DEFAULT_PLAYLIST_IMAGE_PATH =
    "/default-playlist-image.png";

const isLegacyDefaultImagePath = (value: string): boolean => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) return false;
    const defaultPlaylistPath = DEFAULT_PLAYLIST_IMAGE_PATH.toLowerCase();
    if (normalized === defaultPlaylistPath || normalized.endsWith(defaultPlaylistPath)) {
        return true;
    }
    if (normalized.includes("/playlist_images/default.png")) return true;
    return (
        normalized === "default.png" ||
        normalized === "default_thumb.png" ||
        normalized.endsWith("/default.png") ||
        normalized.endsWith("/default_thumb.png") ||
        normalized.endsWith("/default-cover.png") ||
        normalized.endsWith("/default-playlist.png") ||
        normalized.endsWith("/default-playlist-image.png")
    );
};

const isPlaceholderPlaylistImage = (value: string): boolean => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) return true;
    if (normalized === "/placeholder.png" || normalized === "/placeholder.svg") return true;
    return isLegacyDefaultImagePath(normalized);
};

export const handlePlaylistImageError = (imageElement: HTMLImageElement): void => {
    if (imageElement.src.endsWith(DEFAULT_PLAYLIST_IMAGE_PATH)) {
        return;
    }
    imageElement.onerror = null;
    imageElement.src = DEFAULT_PLAYLIST_IMAGE_PATH;
};

export const stripImageCacheBust = (imageUrl?: string | null): string => {
    const raw = String(imageUrl || "").trim();
    if (!raw) return "";

    try {
        if (/^https?:\/\//i.test(raw)) {
            const url = new URL(raw);
            url.searchParams.delete("v");
            return url.toString();
        }

        const [path, query = ""] = raw.split("?", 2);
        if (!query) return raw;
        const params = new URLSearchParams(query);
        params.delete("v");
        const nextQuery = params.toString();
        return nextQuery ? `${path}?${nextQuery}` : path;
    } catch {
        return raw.replace(/([?&])v=\d+(&)?/, (_match, prefix, suffix) => {
            if (prefix === "?" && suffix) return "?";
            if (prefix === "?" && !suffix) return "";
            return suffix ? "&" : "";
        });
    }
};

/* ----------------------------- Image Cache Config ----------------------------- */
const IMAGE_CACHE_KEY = "image_resolution_cache";
const UPLOAD_CACHE_KEY = "image_upload_cache";
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days
const MEMORY_CACHE_LIMIT = 100; // Maximum memory cache entries
const CLEANUP_INTERVAL = 30 * 60 * 1000; // Cleanup every 30 minutes



/* ----------------------------- Mobile Detection ----------------------------- */
const isMobileDevice = (): boolean => {
    if (typeof window === 'undefined') return false;
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

const isIOS = (): boolean => {
    if (typeof window === 'undefined') return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const isAndroid = (): boolean => {
    if (typeof window === 'undefined') return false;
    return /Android/i.test(navigator.userAgent);
};
/* ----------------------------- Enhanced Cache Management ----------------------------- */
interface CacheEntry {
    url: string;
    timestamp: number;
    size?: number; // Estimated size in bytes
    lastAccessed: number; // For LRU eviction
}

class EnhancedImageCache {
    private static instance: EnhancedImageCache;
    private memoryCache = new Map<string, CacheEntry>();
    private cleanupTimer: NodeJS.Timeout | null = null;

    private constructor() {
        this.startPeriodicCleanup();
        this.restoreMemoryCache();
    }

    static getInstance(): EnhancedImageCache {
        if (!EnhancedImageCache.instance) {
            EnhancedImageCache.instance = new EnhancedImageCache();
        }
        return EnhancedImageCache.instance;
    }

    set(key: string, url: string, storageKey: string = IMAGE_CACHE_KEY): void {
        const entry: CacheEntry = {
            url,
            timestamp: Date.now(),
            lastAccessed: Date.now(),
            size: this.estimateEntrySize(key, url)
        };

        // Memory cache with LRU eviction
        if (this.memoryCache.size >= MEMORY_CACHE_LIMIT) {
            this.evictLRU();
        }
        this.memoryCache.set(key, entry);

        // Persistent storage
        try {
            const cache = this.getStorageCache(storageKey);
            cache[key] = entry;
            localStorage.setItem(storageKey, JSON.stringify(cache));
        } catch (error) {
            console.warn("Failed to cache image:", error);
        }
    }

    get(key: string, storageKey: string = IMAGE_CACHE_KEY): string | null {
        // Check memory cache first
        const memoryEntry = this.memoryCache.get(key);
        if (memoryEntry && !this.isExpired(memoryEntry.timestamp)) {
            // Update last accessed time
            memoryEntry.lastAccessed = Date.now();
            return memoryEntry.url;
        }

        // Check persistent storage
        try {
            const cache = this.getStorageCache(storageKey);
            const entry = cache[key];

            if (entry && !this.isExpired(entry.timestamp)) {
                // Promote to memory cache (with LRU eviction if needed)
                if (this.memoryCache.size >= MEMORY_CACHE_LIMIT) {
                    this.evictLRU();
                }

                const enhancedEntry: CacheEntry = {
                    ...entry,
                    lastAccessed: Date.now()
                };

                this.memoryCache.set(key, enhancedEntry);
                return entry.url;
            }

            // Remove expired entry
            if (entry) {
                delete cache[key];
                localStorage.setItem(storageKey, JSON.stringify(cache));
            }
        } catch {
            // Ignore storage errors
        }

        return null;
    }

    private getStorageCache(storageKey: string): Record<string, CacheEntry> {
        try {
            return JSON.parse(localStorage.getItem(storageKey) || '{}');
        } catch {
            return {};
        }
    }

    private isExpired(timestamp: number): boolean {
        return Date.now() - timestamp > CACHE_DURATION;
    }

    private estimateEntrySize(key: string, url: string): number {
        // Rough estimation: key length + URL length + overhead
        return (key.length + url.length) * 2 + 100; // bytes
    }

    private evictLRU(): void {
        let lruKey: string | null = null;
        let oldestAccess = Date.now();

        this.memoryCache.forEach((entry, key) => {
            if (entry.lastAccessed < oldestAccess) {
                oldestAccess = entry.lastAccessed;
                lruKey = key;
            }
        });

        if (lruKey) {
            this.memoryCache.delete(lruKey);
        }
    }

    private restoreMemoryCache(): void {
        // Restore most recently used items to memory cache
        try {
            const cache = this.getStorageCache(IMAGE_CACHE_KEY);
            const entries = Object.entries(cache)
                .filter(([_, entry]) => !this.isExpired(entry.timestamp))
                .sort((a, b) => b[1].timestamp - a[1].timestamp) // Most recent first
                .slice(0, MEMORY_CACHE_LIMIT); // Only restore limit

            entries.forEach(([key, entry]) => {
                this.memoryCache.set(key, {
                    ...entry,
                    lastAccessed: Date.now()
                });
            });
        } catch (error) {
            console.warn("Failed to restore memory cache:", error);
        }
    }

    private startPeriodicCleanup(): void {
        this.cleanupTimer = setInterval(() => {
            this.cleanupExpiredCache();
        }, CLEANUP_INTERVAL);
    }

    private cleanupExpiredCache(): void {
        // Clean up memory cache
        const now = Date.now();
        this.memoryCache.forEach((entry, key) => {
            if (this.isExpired(entry.timestamp)) {
                this.memoryCache.delete(key);
            }
        });

        // Clean up persistent storage
        [IMAGE_CACHE_KEY, UPLOAD_CACHE_KEY].forEach(storageKey => {
            try {
                const cache = this.getStorageCache(storageKey);
                let hasChanges = false;

                Object.keys(cache).forEach(key => {
                    if (this.isExpired(cache[key].timestamp)) {
                        delete cache[key];
                        this.memoryCache.delete(key);
                        hasChanges = true;
                    }
                });

                if (hasChanges) {
                    localStorage.setItem(storageKey, JSON.stringify(cache));
                }
            } catch (error) {
                console.warn("Failed to cleanup cache:", error);
            }
        });
    }

    clear(storageKey?: string): void {
        if (storageKey) {
            // Clear specific storage
            this.memoryCache.forEach((_, key) => {
                if (key.startsWith(storageKey.replace(/_cache$/, ''))) {
                    this.memoryCache.delete(key);
                }
            });
            try {
                localStorage.removeItem(storageKey);
            } catch {
                // Ignore errors
            }
        } else {
            // Clear everything
            this.memoryCache.clear();
            try {
                localStorage.removeItem(IMAGE_CACHE_KEY);
                localStorage.removeItem(UPLOAD_CACHE_KEY);
            } catch {
                // Ignore errors
            }
        }
    }

    getStats() {
        const memorySize = this.memoryCache.size;
        const storageCache = this.getStorageCache(IMAGE_CACHE_KEY);
        const uploadCache = this.getStorageCache(UPLOAD_CACHE_KEY);
        const storageSize = Object.keys(storageCache).length;
        const uploadCacheSize = Object.keys(uploadCache).length;

        // Calculate memory usage estimation
        let memoryUsage = 0;
        this.memoryCache.forEach(entry => {
            memoryUsage += entry.size || 0;
        });

        return {
            memorySize,
            storageSize,
            uploadCacheSize,
            memoryUsage: Math.round(memoryUsage / 1024) // Convert to KB
        };
    }

    // New method to get cache keys for debugging
    getCacheKeys(): { memory: string[], storage: string[], upload: string[] } {
        return {
            memory: Array.from(this.memoryCache.keys()),
            storage: Object.keys(this.getStorageCache(IMAGE_CACHE_KEY)),
            upload: Object.keys(this.getStorageCache(UPLOAD_CACHE_KEY))
        };
    }

    destroy(): void {
        if (this.cleanupTimer) {
            clearInterval(this.cleanupTimer);
            this.cleanupTimer = null;
        }
    }
}

// Singleton instance
const imageCache = EnhancedImageCache.getInstance();

/* ----------------------------- Enhanced R2 Image Resolution ----------------------------- */
export const resolveImage = (u?: string | null, cacheBust = false): string => {
    // Return R2 default image if no input
    if (!u || u.trim() === "") {
        return getR2DefaultImage();
    }

    u = u.trim();

    if (isLegacyDefaultImagePath(u)) {
        return getR2DefaultImage();
    }

    // Keep base64 and blob URLs as-is
    if (u.startsWith("data:image") || u.startsWith("blob:")) {
        return u;
    }

    // Check cache first (use normalized key)
    const normalizedInput = u.toLowerCase();
    const cacheKey = `resolve_${normalizedInput}`;

    const cached = imageCache.get(cacheKey, IMAGE_CACHE_KEY);
    if (cached) {
        const isMixedContentCache =
            typeof window !== "undefined" &&
            window.location.protocol === "https:" &&
            /^http:\/\//i.test(cached);

        if (!isMixedContentCache) {
            return cacheBust ? addCacheBust(cached) : cached;
        }
    }

    // Resolve to R2 URL
    let resolvedUrl: string;

    // Already an R2 URL
    if (u.includes('r2.dev')) {
        resolvedUrl = u;
    }
    // Already a full URL (not R2)
    else if (u.startsWith("http://") || u.startsWith("https://")) {
        // Keep uploaded assets on backend same-origin proxy to avoid mixed content.
        if (u.includes('/uploads/logos/')) {
            const filename = u.split('/').pop();
            resolvedUrl = `/uploads/logos/${filename}`;
        } else {
            resolvedUrl = u;
        }
    }
    // Relative path - convert to R2 URL
    else if (u.startsWith('/')) {
        // Special case: default images
        if (u === '/default-playlist.png' || u === '/default-playlist-image.png') {
            resolvedUrl = getR2DefaultImage();
        }
        else if (u === "/QIT TRENDING.png") {
            resolvedUrl = u;
        }
        // Backend-served static/upload assets (keep backward compatibility).
        else if (u.startsWith("/uploads/")) {
            resolvedUrl = toStaticServiceUrl("playlist", u);
        }
        else if (u.startsWith("/static/")) {
            resolvedUrl = buildApiUrl(u);
        }
        // Other relative paths → R2
        else {
            resolvedUrl = `${R2_CONFIG.PUBLIC_BASE_URL}${u}`;
        }
    }
    // Relative backend path without leading slash
    else if (u.startsWith("uploads/")) {
        resolvedUrl = toStaticServiceUrl("playlist", `/${u.replace(/^\/+/, "")}`);
    }
    else if (u.startsWith("static/")) {
        resolvedUrl = buildApiUrl(`/${u.replace(/^\/+/, "")}`);
    }
    // Just filename - assume it's in playlist_images
    else {
        resolvedUrl = `${R2_CONFIG.PUBLIC_BASE_URL}${R2_CONFIG.PLAYLIST_IMAGES_PATH}/${u}`;
    }

    // Save to cache
    imageCache.set(cacheKey, resolvedUrl, IMAGE_CACHE_KEY);

    return cacheBust ? addCacheBust(resolvedUrl) : resolvedUrl;
};

export const getAiPlaylistImage = (_playlistKey?: string, cacheBust = false): string => {
    return resolveImage(DEFAULT_PLAYLIST_IMAGE_PATH, cacheBust);
};

export const resolvePlaylistImage = (
    imageUrl?: string | null,
    cacheBust = false,
    _playlistKey?: string
): string => {
    if (!imageUrl || isPlaceholderPlaylistImage(imageUrl)) {
        return resolveImage(DEFAULT_PLAYLIST_IMAGE_PATH, cacheBust);
    }
    return resolveImage(imageUrl, cacheBust);
};

/* ----------------------------- R2 Specialized Helpers ----------------------------- */
export const getR2DefaultImage = (): string => {
    return DEFAULT_PLAYLIST_IMAGE_PATH;
};

export const getR2FlagUrl = (countryCode: string, cacheBust = false): string => {
    const code = (countryCode || 'us').toLowerCase().trim();
    const flagUrl = `${R2_CONFIG.PUBLIC_BASE_URL}${R2_CONFIG.FLAGS_PATH}/${code}.png`;
    return cacheBust ? addCacheBust(flagUrl) : flagUrl;
};

export const getR2PlaylistImage = (imagePath?: string | null, cacheBust = false): string => {
    return resolveImage(imagePath, cacheBust);
};

/* ----------------------------- Enhanced Image Upload with Caching ----------------------------- */
/* ----------------------------- Enhanced Image Upload with Caching ----------------------------- */
/* ----------------------------- Enhanced Image Upload with Caching ----------------------------- */
export const uploadImage = async (file: File, onProgress?: (progress: number) => void): Promise<string> => {
    const isMobile = isMobileDevice();

    let uploadFile = file;
    
    if (isMobile) {
        console.log("📱 MOBILE: Forcing JPEG with .jpg extension");
        // Get processed BLOB, not File
        const processedBlob = await processFileForMobile(file);
        // Create ONE File from blob (no nesting)
        uploadFile = new File([processedBlob], "upload.jpg", {
            type: 'image/jpeg',
            lastModified: Date.now()
        });
    }

    const cacheKey = await generateFileHash(uploadFile);
    const cached = imageCache.get(cacheKey, UPLOAD_CACHE_KEY);
    if (cached) {
        console.log("🔄 Using cached uploaded image:", file.name);
        onProgress?.(100);
        return cached;
    }

    const token = localStorage.getItem("authToken") || "";
    const formData = new FormData();

    if (isMobile) {
        formData.append("logo", uploadFile, "upload.jpg");
    } else {
        formData.append("logo", file);
    }

    try {
        onProgress?.(0);
        console.log("📤 Uploading to:", buildApiUrl("/api/v1/playlists/upload"));

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), isMobile ? 60000 : 15000); // 60s on mobile
        const res = await fetch(buildApiUrl("/api/v1/playlists/upload"), {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
            signal: controller.signal
        });

        clearTimeout(timeoutId);
        onProgress?.(50);

        if (!res.ok) {
            const text = await res.text();
            console.error("❌ Upload failed:", res.status, text);
            throw new Error(`Upload failed: ${res.status} ${text}`);
        }

        const data = await res.json();
        console.log("📥 Server response:", data);

        let imageUrl: string | undefined;
        const possibleFields = ['url', 'path', 'logoUrl', 'thumbUrl', 'imageUrl'];

        for (const field of possibleFields) {
            if (data[field] && typeof data[field] === 'string') {
                imageUrl = data[field];
                break;
            }
        }

        if (!imageUrl) throw new Error("No image URL in response");

        if (isMobile && imageUrl.includes('r2.dev')) {
            imageUrl = addCacheBust(imageUrl);
        }

        onProgress?.(100);
        imageCache.set(cacheKey, imageUrl, UPLOAD_CACHE_KEY);
        return imageUrl;

    } catch (error) {
        onProgress?.(0);
        console.error("❌ Upload error:", error);
        throw error;
    }
};


/* ----------------------------- Mobile File Processing ----------------------------- */
const processFileForMobile = async (file: File): Promise<Blob> => {
    console.log("📱 MOBILE: Processing file for mobile upload...");

    // iOS: Handle HEIC format
    if (isIOS() && (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic'))) {
        console.log("🔄 MOBILE: iOS HEIC image detected");
        try {
            return await convertHeicToJpeg(file);
        } catch (error) {
            console.error("❌ HEIC conversion failed:", error);
            // MUST convert to JPEG even if conversion fails
            return file; // <-- JUST RETURN THE ORIGINAL FILE // <-- THIS LINE FIXES IT
        }
    }

    // MOBILE FIX: Compress large images
    if (file.size > 2 * 1024 * 1024) { // 2MB threshold for mobile
        console.log("📱 MOBILE: Compressing large image", {
            originalSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`
        });

        try {
            const compressedBlob = await compressImageForMobile(file);
            console.log("✅ MOBILE: Image compressed", {
                original: `${(file.size / 1024).toFixed(2)}KB`,
                compressed: `${(compressedBlob.size / 1024).toFixed(2)}KB`,
                reduction: `${((1 - compressedBlob.size / file.size) * 100).toFixed(1)}%`
            });
            return compressedBlob;
        } catch (error) {
            console.error("❌ Mobile compression failed:", error);
            return file;
        }
    }

    return file;
};

const compressImageForMobile = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();

        reader.onload = (e) => {
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Canvas context failed'));
                    return;
                }

                // Mobile-optimized dimensions
                const MAX_WIDTH = 1024;
                const MAX_HEIGHT = 1024;

                let width = img.width;
                let height = img.height;

                // Calculate new dimensions while maintaining aspect ratio
                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height = Math.round((height * MAX_WIDTH) / width);
                        width = MAX_WIDTH;
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width = Math.round((width * MAX_HEIGHT) / height);
                        height = MAX_HEIGHT;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                // Draw with high quality
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to JPEG blob (not File)
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('Canvas to Blob failed'));
                            return;
                        }
                        resolve(blob); // Return Blob, not File
                    },
                    'image/jpeg',
                    isIOS() ? 0.8 : 0.7 // Higher quality for iOS
                );
            };

            img.onerror = () => reject(new Error('Image loading failed'));
            img.src = e.target?.result as string;
        };

        reader.onerror = () => reject(new Error('File reading failed'));
        reader.readAsDataURL(file);
    });
};

// Simple HEIC to JPEG converter - returns Blob
const convertHeicToJpeg = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {  // <-- CHANGE: Add reject parameter
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Canvas context failed'));  // <-- ADD ERROR HANDLING
                    return;
                }
                ctx.drawImage(img, 0, 0);
                canvas.toBlob(
                    (blob) => blob ? resolve(blob) : reject(new Error('Blob creation failed')),
                    'image/jpeg',
                    0.9
                );
            };
            img.onerror = () => reject(new Error('Image loading failed'));  // <-- ADD ERROR HANDLING
            img.src = reader.result as string;
        };
        reader.onerror = () => reject(new Error('File reading failed'));  // <-- ADD ERROR HANDLING
        reader.readAsDataURL(file);
    });
};

const forceConvertToJpeg = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Canvas context failed'));
                    return;
                }
                ctx.drawImage(img, 0, 0);
                canvas.toBlob(
                    (blob) => blob ? resolve(blob) : reject(new Error('Blob creation failed')),
                    'image/jpeg',
                    0.8
                );
            };
            img.onerror = () => reject(new Error('Image loading failed'));
            img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error('File reading failed'));
        reader.readAsDataURL(file);
    });
};

/* ----------------------------- Utility Functions ----------------------------- */
const addCacheBust = (url: string): string => {
    const version = Date.now().toString();

    try {
        const parsed = new URL(
            url,
            typeof window !== "undefined" ? window.location.origin : "http://localhost"
        );
        parsed.searchParams.set("v", version);

        // Preserve relative URLs when the original input was relative.
        if (!/^https?:\/\//i.test(url)) {
            return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }

        return parsed.toString();
    } catch {
        const joiner = url.includes("?") ? "&" : "?";
        return `${url}${joiner}v=${version}`;
    }
};

export const isR2Url = (url: string): boolean => {
    return url.includes('r2.dev');
};

export const extractR2Filename = (url: string): string | null => {
    if (!isR2Url(url)) return null;
    try {
        const urlObj = new URL(url);
        return urlObj.pathname.split('/').pop() || null;
    } catch {
        return null;
    }
};

/* ----------------------------- Enhanced File Hash Generation ----------------------------- */
const generateFileHash = async (file: File): Promise<string> => {
    // For small files, use content hash for accuracy
    if (file.size < 5 * 1024 * 1024) { // 5MB limit
        try {
            const arrayBuffer = await file.arrayBuffer();
            const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const contentHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            return `content_${contentHash}`;
        } catch (error) {
            console.warn("Failed to generate content hash, using basic hash:", error);
        }
    }

    // Fallback to basic hash for large files
    return `file_${file.name}_${file.size}_${file.lastModified}_${file.type}`;
};

/* ----------------------------- Enhanced Preloading with Error Handling ----------------------------- */
export const preloadImages = async (imageUrls: string[], signal?: AbortSignal): Promise<{ loaded: number; failed: number }> => {
    let loaded = 0;
    let failed = 0;

    const preloadPromises = imageUrls.map(url => {
        return new Promise<void>((resolve) => {
            if (signal?.aborted) {
                resolve();
                return;
            }

            const img = new Image();

            img.onload = () => {
                loaded++;
                resolve();
            };

            img.onerror = () => {
                failed++;
                console.warn("Failed to preload image:", url);
                resolve();
            };

            img.src = resolveImage(url);

            // Handle abort signal
            if (signal) {
                signal.addEventListener('abort', () => {
                    resolve();
                });
            }
        });
    });

    await Promise.all(preloadPromises);
    return { loaded, failed };
};

export const batchResolveImages = (urls: (string | null | undefined)[]): string[] => {
    return urls.map(url => resolveImage(url));
};

/* ----------------------------- Enhanced Cache Utilities ----------------------------- */
export const imageCacheUtils = {
    // R2-specific functions
    getR2DefaultImage,
    getR2FlagUrl,
    getR2PlaylistImage,
    getAiPlaylistImage,
    resolvePlaylistImage,
    handlePlaylistImageError,
    stripImageCacheBust,
    isR2Url,
    extractR2Filename,

    // Cache management
    clearCache: (storageKey?: string) => imageCache.clear(storageKey),
    getCacheStats: () => imageCache.getStats(),
    getCacheKeys: () => imageCache.getCacheKeys(),

    // Preloading
    preloadImages,
    batchResolveImages,

    // Direct cache access
    cacheImage: (key: string, url: string) => imageCache.set(key, url, IMAGE_CACHE_KEY),
    getCachedImage: (key: string) => imageCache.get(key, IMAGE_CACHE_KEY),

    // Upload cache management
    clearUploadCache: () => imageCache.clear(UPLOAD_CACHE_KEY),

    // Memory management
    optimizeMemory: () => {
        // Force LRU eviction if over limit
        while (imageCache.getStats().memorySize > MEMORY_CACHE_LIMIT) {
            // This will trigger LRU eviction on next set operation
            imageCache.set('temp_cleanup', 'temp');
            imageCache.clear('temp_cleanup');
        }
    },

    // Cleanup
    destroy: () => imageCache.destroy()
};

/* ----------------------------- Export Everything ----------------------------- */
// For backward compatibility, export the main functions

// Also export as a unified object
export default {
    resolveImage,
    resolvePlaylistImage,
    getAiPlaylistImage,
    uploadImage,
    getR2DefaultImage,
    getR2FlagUrl,
    getR2PlaylistImage,

    isMobileDevice,
    isIOS,
    isAndroid,

    ...imageCacheUtils
};
